import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["active", "color", "endColor", "focused", "hovered", "opacity", "startColor", "size"],
  _excluded2 = ["angle", "color", "endColor", "label", "opacity", "selected", "size", "startColor", "children"];
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import devLogger from 'react-utils/devLogger';
import { CALYPSO, CALYPSO_MEDIUM, MONOLITH, OLAF } from 'HubStyleTokens/colors';
import { setUiTransition, toPx } from '../utils/Styles';
import { hidden } from '../utils/propTypes/decorators';
import { getGradient } from './gradients/Styles';
import { rgba } from '../core/Color';
import ActiveProvider from '../providers/ActiveProvider';
import HoverProvider from '../providers/HoverProvider';
import UIClickable from '../button/UIClickable';
import FocusProvider from '../providers/FocusProvider';

/**
 * @internal
 * **Do not use.** Ask about your use case in #hds-support.
 */
import { jsx as _jsx } from "react/jsx-runtime";
const CHECKERBOARD_IMAGE = `
  <svg height="16" width="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
    <g fill-rule="nonzero" fill="none">
      <path fill="%23F2F5F8" d="M0 0h16v16H0z"/>
      <g fill="%23CBD6E2">
        <path d="M8 8h8v8H8zM0 0h8v8H0z"/>
      </g>
    </g>
  </svg>
`;
const SQUATCH_SIZE = 28;
const getSelectedShadow = `
  box-shadow: 0 0 0 1px ${OLAF}, 0 0 0 3px ${CALYPSO};
`;
const getHoverShadow = `
  box-shadow: 0 0 0 2px ${CALYPSO_MEDIUM},
    0 0 8px 0 ${rgba(CALYPSO, 0.3)};
`;
const getOpacityAffordance = (size = SQUATCH_SIZE) => css(["background-image:url('data:image/svg+xml;utf8,", "');background-repeat:repeat;background-size:", ";"], CHECKERBOARD_IMAGE, toPx(size / 2.5));
const SquatchOuter = styled(props => {
  const rest = _objectWithoutPropertiesLoose(props, _excluded);
  return /*#__PURE__*/_jsx(UIClickable, Object.assign({}, rest));
}).withConfig({
  displayName: "UIColorSquatch__SquatchOuter",
  componentId: "sc-1wanrh8-0"
})(["", ";border-radius:2px;", ";", ";height:", ";position:relative;", ";width:", ";&::after{background-color:", ";border-radius:inherit;bottom:0;content:'';display:", ";left:0;position:absolute;right:0;top:0;}"], props => props.opacity && getOpacityAffordance(props.size), props => props.selected && getSelectedShadow, props => props.hovered && !props.selected && getHoverShadow, props => toPx(props.size), setUiTransition('box-shadow'), props => toPx(props.size), rgba(MONOLITH, 0.1), props => props.active ? 'block' : 'none');
const SquatchColor = styled.span.withConfig({
  displayName: "UIColorSquatch__SquatchColor",
  componentId: "sc-1wanrh8-1"
})(["background:", ";", ";border-width:1px;border-color:", ";border-radius:inherit;border-style:solid;display:block;height:100%;opacity:", ";pointer-events:none;width:100%;"], props => props.color && `${props.color} !important`, props => props.startColor && getGradient(undefined, props.angle, props.startColor, undefined, props.endColor, undefined), props => props.selected ? OLAF : rgba(MONOLITH, 0.2), props => props.opacity && props.opacity / 100);
export default function UIColorSquatch(props) {
  const {
      angle = '90deg',
      color,
      endColor,
      label,
      opacity,
      selected,
      size = SQUATCH_SIZE,
      startColor,
      children
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded2);
  const implicitLabel = label || color;
  const colorOrLabel = !label && startColor && endColor ? `${startColor}, ${endColor}` : implicitLabel;
  const computedLabel = `${colorOrLabel}${opacity ? `, ${opacity} opacity` : ''}`;
  if (process.env.NODE_ENV !== 'production') {
    if (startColor && !endColor) {
      devLogger.warn({
        message: 'UIColorSquatch: Please set an endColor if using a startColor',
        key: 'UIColorSquatch: no startColor without endColor'
      });
    }
    if (!startColor && endColor) {
      devLogger.warn({
        message: 'UIColorSquatch: Please set a startColor if using an endColor',
        key: 'UIColorSquatch: no endColor without startColor'
      });
    }
  }
  return /*#__PURE__*/_jsx(HoverProvider, Object.assign({}, props, {
    children: hoverProviderProps => /*#__PURE__*/_jsx(ActiveProvider, Object.assign({}, props, {
      children: activeProviderProps => /*#__PURE__*/_jsx(FocusProvider, Object.assign({}, props, {
        children: focusProviderProps => /*#__PURE__*/_jsx(SquatchOuter, Object.assign({}, rest, hoverProviderProps, activeProviderProps, focusProviderProps, {
          size: size,
          "aria-label": computedLabel,
          "aria-pressed": selected,
          opacity: opacity,
          selected: selected,
          children: /*#__PURE__*/_jsx(SquatchColor, {
            angle: angle,
            color: color,
            endColor: endColor,
            opacity: opacity,
            startColor: startColor,
            children: children
          })
        }))
      }))
    }))
  }));
}
UIColorSquatch.propTypes = {
  children: PropTypes.node,
  angle: PropTypes.string,
  color: PropTypes.string,
  endColor: PropTypes.string,
  focused: hidden(PropTypes.bool),
  hovered: hidden(PropTypes.bool),
  label: PropTypes.string,
  opacity: PropTypes.number,
  selected: PropTypes.bool,
  size: PropTypes.number,
  startColor: PropTypes.string
};

/* eslint-disable-next-line hubspot-dev/no-default-props-assignment */
UIColorSquatch.defaultProps = {
  angle: '90deg',
  size: SQUATCH_SIZE
};
UIColorSquatch.displayName = 'UIColorSquatch';